/**
 * Dialog showing canonical piece details with all linked suggestions.
 */

import { useState } from 'react'
import { useCanonicalPieceDetails, useUnlinkSuggestion } from '../../hooks/useCanonicalPieces'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { Card, CardContent } from '@brent/core-ui/ui'
import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { Badge } from '@brent/core-ui/ui'
import {
  Loader2,
  UserCircle,
  Calendar,
  MessageSquare,
  Unlink,
  Edit,
  ArrowUpRight,
} from 'lucide-react'
import { useToast } from '../../hooks/use-toast'
import { formatDate } from '../../utils/format'
import type { CanonicalPiece, LinkedSuggestion } from '../../types/canonical-piece'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@brent/core-ui/ui'
import { EditCanonicalPieceDialog } from './EditCanonicalPieceDialog'
import { PromoteCanonicalPieceDialog } from './PromoteCanonicalPieceDialog'

interface CanonicalPieceDetailsDialogProps {
  canonicalPiece: CanonicalPiece
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CanonicalPieceDetailsDialog({
  canonicalPiece,
  open,
  onOpenChange,
}: CanonicalPieceDetailsDialogProps) {
  const { data, isLoading, error } = useCanonicalPieceDetails(canonicalPiece.canonical_piece_id)
  const unlinkMutation = useUnlinkSuggestion()
  const { toast } = useToast()

  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false)
  const [suggestionToUnlink, setSuggestionToUnlink] = useState<LinkedSuggestion | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false)

  const handleUnlinkClick = (suggestion: LinkedSuggestion) => {
    setSuggestionToUnlink(suggestion)
    setUnlinkConfirmOpen(true)
  }

  const handleUnlinkConfirm = async () => {
    if (!suggestionToUnlink) return

    try {
      await unlinkMutation.mutateAsync({
        canonicalPieceId: canonicalPiece.canonical_piece_id,
        suggestionId: suggestionToUnlink.suggestion_id,
      })

      toast({
        title: 'Suggestion unlinked',
        description: 'The suggestion has been unlinked from this canonical piece.',
      })

      setUnlinkConfirmOpen(false)
      setSuggestionToUnlink(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to unlink suggestion',
        description: err instanceof Error ? err.message : 'An error occurred',
      })
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <DialogTitle>{canonicalPiece.work_title || 'Composer-only entry'}</DialogTitle>
                <DialogDescription>
                  <span className="font-medium">{canonicalPiece.composer_name}</span>
                  {' • '}
                  <span className="text-sm">
                    {canonicalPiece.suggestion_count}{' '}
                    {canonicalPiece.suggestion_count === 1 ? 'member' : 'members'} suggested this
                  </span>
                </DialogDescription>
              </div>
              <div className="flex gap-2">
                {!canonicalPiece.work_wikidata_id && (
                  <Button variant="outline" size="sm" onClick={() => setPromoteDialogOpen(true)}>
                    <ArrowUpRight className="h-4 w-4 mr-2" />
                    Promote to Wikidata
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => setEditDialogOpen(true)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </div>
          </DialogHeader>

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>Failed to load details: {error.message}</AlertDescription>
            </Alert>
          )}

          {data && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium mb-3">Linked Suggestions</h3>
                <div className="space-y-2">
                  {data.suggestions.map((suggestion) => (
                    <Card key={suggestion.suggestion_id}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                              <UserCircle className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{suggestion.user_name}</span>
                              <Badge variant="outline" className="text-xs">
                                <Calendar className="h-3 w-3 mr-1" />
                                {formatDate(suggestion.created_at)}
                              </Badge>
                            </div>

                            {suggestion.comment && (
                              <div className="flex gap-2 text-sm text-muted-foreground">
                                <MessageSquare className="h-4 w-4 shrink-0 mt-0.5" />
                                <p>{suggestion.comment}</p>
                              </div>
                            )}

                            {!suggestion.comment && (
                              <p className="text-sm text-muted-foreground italic">
                                No comment provided
                              </p>
                            )}
                          </div>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnlinkClick(suggestion)}
                            disabled={unlinkMutation.isPending}
                          >
                            <Unlink className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {data.suggestions.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No suggestions linked to this canonical piece.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={unlinkConfirmOpen} onOpenChange={setUnlinkConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink suggestion?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the canonical piece link from the suggestion. The suggestion will
              keep its Wikidata IDs but will no longer be grouped with other suggestions for this
              piece.
              <br />
              <br />
              This action can be undone by linking the suggestion again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnlinkConfirm}>Unlink</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditCanonicalPieceDialog
        canonicalPiece={canonicalPiece}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={() => {
          // Close details dialog after successful edit (ID may have changed)
          setEditDialogOpen(false)
          onOpenChange(false)
        }}
      />

      <PromoteCanonicalPieceDialog
        canonicalPiece={canonicalPiece}
        open={promoteDialogOpen}
        onOpenChange={setPromoteDialogOpen}
        onSuccess={() => {
          setPromoteDialogOpen(false)
          onOpenChange(false)
        }}
      />
    </>
  )
}
